import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { UserLeave, UserApplyLeave } from '../../../../services/oprations/User_Bussiness_Logic';
import {
  Calendar,
  CalendarCheck2,
  AlertCircle,
  X,
  Search,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import LeaveModal from './LeaveApplyModal';
import HolidaysModal from './HolidaysModal';

// Toast Component
const Toast = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 3000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div
      className={`fixed top-4 right-4 left-4 sm:left-auto z-50 flex items-center gap-3 px-4 py-3 rounded-lg shadow-lg animate-slide-in ${
        type === 'success'
          ? 'bg-green-50 dark:bg-green-900/90 border border-green-200 dark:border-green-700'
          : 'bg-red-50 dark:bg-red-900/90 border border-red-200 dark:border-red-700'
      }`}
    >
      <div
        className={`text-sm font-medium flex-1 ${
          type === 'success'
            ? 'text-green-800 dark:text-green-200'
            : 'text-red-800 dark:text-red-200'
        }`}
      >
        {message}
      </div>
      <button
        onClick={onClose}
        className="flex-shrink-0 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};

// Mobile leave request card
const LeaveCard = ({ request, formatDate, getStatusColor }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="p-4 space-y-2 bg-white border border-gray-100 rounded-lg dark:border-gray-800 dark:bg-gray-900">
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="text-sm font-medium text-gray-800 dark:text-white">
            {request.leaveType}
            {request.leaveDuration === 'Half Day' && (
              <span className="ml-1.5 text-xs font-normal text-gray-400 dark:text-gray-500">(Half Day)</span>
            )}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            {formatDate(request.fromDate)} → {formatDate(request.toDate)}
          </p>
        </div>
        <span className={`inline-flex px-2 py-1 text-xs rounded-full flex-shrink-0 ${getStatusColor(request.status)}`}>
          {request.status || 'PENDING'}
        </span>
      </div>

      <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
        <span>{request.days} day{request.days !== 1 ? 's' : ''}</span>
        <span>Applied: {formatDate(request.appliedAt || request.createdAt)}</span>
      </div>

      {/* Expandable reason */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-1 text-xs text-blue-500 hover:text-blue-600 dark:text-blue-400"
      >
        {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
        {expanded ? 'Hide reason' : 'Show reason'}
      </button>
      {expanded && (
        <p className="p-2 text-xs text-gray-600 rounded dark:text-gray-300 bg-gray-50 dark:bg-gray-800">
          {request.reason}
        </p>
      )}
    </div>
  );
};

const LeaveManagement = () => {
  const dispatch = useDispatch();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isHolidayModalOpen, setIsHolidayModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const [formData, setFormData] = useState({
    leaveType: '',
    leaveDuration: 'Full Day',
    fromDate: '',
    toDate: '',
    reason: ''
  });
  const [errors, setErrors] = useState({});

  const [searchTerm, setSearchTerm] = useState('');
  const [searchField, setSearchField] = useState('all');
  const [filteredLeaveData, setFilteredLeaveData] = useState([]);

  const { userDetails } = useSelector((state) => state.employeeAuth?.data || state.employeeAuth || {});
  const { leaveData } = useSelector((state) => state.userLeave?.data || state.userLeave || []);
  const { holidaysData } = useSelector((state) => state.userHolidays?.data || state.userHolidays || []);

  const token = localStorage.getItem('token') || userDetails?.token;

   const fetchLeaveData = useCallback(async () => {
    if (token) await dispatch(UserLeave(token));
  },[dispatch, token]);

     const filterLeaveRequests =useCallback( () => {
    if (!leaveData || leaveData.length === 0) { setFilteredLeaveData([]); return; }
    if (!searchTerm.trim()) { setFilteredLeaveData(leaveData); return; }
    const searchLower = searchTerm.toLowerCase().trim();
    const filtered = leaveData.filter((request) => {
      switch (searchField) {
        case 'type': return request.leaveType?.toLowerCase().includes(searchLower);
        case 'status': return request.status?.toLowerCase().includes(searchLower);
        case 'reason': return request.reason?.toLowerCase().includes(searchLower);
        case 'date': {
          const fromDate = formatDateForSearch(request.fromDate);
          const toDate = formatDateForSearch(request.toDate);
          const appliedDate = formatDateForSearch(request.appliedAt || request.createdAt);
          return fromDate.includes(searchLower) || toDate.includes(searchLower) || appliedDate.includes(searchLower);
        }
        default:
          return (
            request.leaveType?.toLowerCase().includes(searchLower) ||
            request.status?.toLowerCase().includes(searchLower) ||
            request.reason?.toLowerCase().includes(searchLower) ||
            formatDateForSearch(request.fromDate).includes(searchLower) ||
            formatDateForSearch(request.toDate).includes(searchLower) ||
            formatDateForSearch(request.appliedAt || request.createdAt).includes(searchLower) ||
            request.days?.toString().includes(searchLower)
          );
      }
    });
    setFilteredLeaveData(filtered);
  },[leaveData, searchField, searchTerm]);

  useEffect(() => {
    if (token) fetchLeaveData();
  }, [token, fetchLeaveData]);

  useEffect(() => {
    filterLeaveRequests();
  }, [searchTerm, searchField, leaveData, filterLeaveRequests]);


  // Half Day is always 0.5, regardless of the dates.
  // Full Day counts working days (Mon–Fri) inclusive, same as before.
  const calculateDays = (fromDate, toDate, leaveDuration = formData.leaveDuration) => {
    if (leaveDuration === 'Half Day') return 0.5;
    if (!fromDate || !toDate) return 0;
    let count = 0;
    let current = new Date(fromDate);
    const end = new Date(toDate);
    current.setHours(0, 0, 0, 0);
    end.setHours(0, 0, 0, 0);
    while (current <= end) {
      const day = current.getDay();
      if (day !== 0 && day !== 6) count++;
      current.setDate(current.getDate() + 1);
    }
    return count;
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.leaveType) newErrors.leaveType = 'Please select leave type';
    if (!formData.fromDate) newErrors.fromDate = 'Please select start date';
    if (!formData.toDate) newErrors.toDate = 'Please select end date';

    if (formData.leaveDuration === 'Half Day') {
      // Half Day must always be a single day
      if (formData.fromDate && formData.toDate && formData.fromDate !== formData.toDate) {
        newErrors.toDate = 'To date must match From date for a half day leave';
      }
    } else if (
      formData.fromDate &&
      formData.toDate &&
      new Date(formData.fromDate) > new Date(formData.toDate)
    ) {
      newErrors.toDate = 'End date must be after start date';
    }

    if (!formData.reason) newErrors.reason = 'Please provide a reason';
    if (formData.reason && formData.reason.length < 10) {
      newErrors.reason = 'Reason must be at least 10 characters';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setLoading(true);

    const leaveDuration = formData.leaveDuration || 'Full Day';
    // Half Day always submits with toDate === fromDate, just in case state drifted
    const toDate = leaveDuration === 'Half Day' ? formData.fromDate : formData.toDate;
    const days = calculateDays(formData.fromDate, toDate, leaveDuration);

    const payload = { ...formData, toDate, leaveDuration, days };

    try {
      const result = await dispatch(UserApplyLeave(payload, token));
      if (result?.data?.success) {
        setToast({ show: true, message: 'Leave request submitted successfully!', type: 'success' });
        await fetchLeaveData();
        setFormData({ leaveType: '', leaveDuration: 'Full Day', fromDate: '', toDate: '', reason: '' });
        setErrors({});
        setSearchTerm('');
      } else {
        setToast({ show: true, message: result?.data?.message || 'Failed to submit leave request.', type: 'error' });
      }
    } catch (err) {
      setToast({ show: true, message: 'An error occurred. Please try again.', type: 'error' });
    } finally {
      setLoading(false);
      setIsModalOpen(false);
    }
  };



  const formatDateForSearch = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }).toLowerCase();
  };

  const getStatusColor = (status) => {
    switch (status?.toUpperCase()) {
      case 'APPROVED': return 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400';
      case 'PENDING': return 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400';
      case 'REJECTED': return 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400';
      default: return 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-400';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const calculateLeaveBalances = () => {
    const approved = leaveData.filter(l => l.status?.toUpperCase() === 'APPROVED');
    const annualUsed = approved.filter(l => l.leaveType === 'Annual Leave').reduce((s, l) => s + (l.days || 0), 0);
    const sickUsed = approved.filter(l => l.leaveType === 'Sick Leave').reduce((s, l) => s + (l.days || 0), 0);
    const casualUsed = approved.filter(l => l.leaveType === 'Casual Leave').reduce((s, l) => s + (l.days || 0), 0);
    const pendingCount = leaveData.filter(l => l.status?.toUpperCase() === 'PENDING').length;
    const rejectedCount = leaveData.filter(l => l.status?.toUpperCase() === 'REJECTED').length;
    return {
      annual: { used: annualUsed, total: 20 },
      sick: { used: sickUsed, total: 10 },
      casual: { used: casualUsed, total: 6 },
      pending: pendingCount,
      rejected: rejectedCount,
      approved: approved.length,
    };
  };

  const getHolidaysByMonth = () => {
    const holidaysByMonth = {};
    holidaysData.forEach(holiday => {
      const date = new Date(holiday.DateString);
      const monthYear = date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
      if (!holidaysByMonth[monthYear]) holidaysByMonth[monthYear] = [];
      holidaysByMonth[monthYear].push({
        name: holiday.Holidayname,
        date,
        formattedDate: formatDate(holiday.DateString)
      });
    });
    Object.keys(holidaysByMonth).forEach(month => {
      holidaysByMonth[month].sort((a, b) => a.date - b.date);
    });
    return holidaysByMonth;
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setSearchField('all');
  };

  const balances = calculateLeaveBalances();
  const holidaysByMonth = getHolidaysByMonth();

  const balanceCards = [
    {
      title: 'Total Leave',
      value: userDetails?.leaveBalance,
      color: 'from-purple-500 to-purple-600 dark:from-purple-600 dark:to-purple-700'
    },
    {
      title: 'Approved Leave',
      value: balances.approved,
      color: 'from-blue-500 to-blue-600 dark:from-blue-600 dark:to-blue-700'
    },
    {
      title: 'Rejected Leave',
      value: balances.rejected,
      color: 'from-green-500 to-green-600 dark:from-green-600 dark:to-green-700'
    },
    {
      title: 'Pending Leave',
      value: balances.pending,
      color: 'from-blue-500 to-blue-500 dark:from-blue-600 dark:to-blue-600'
    }
  ];

  return (
    <div className="space-y-4 sm:space-y-6">
      {toast.show && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ show: false, message: '', type: 'success' })}
        />
      )}

      {/* Header */}
      <div className="mt-3">
        <h1 className="text-lg font-semibold text-gray-900 sm:text-xl dark:text-white">Leave & Holidays</h1>
        <p className="mt-1 text-xs text-gray-500 sm:text-sm dark:text-gray-400">
          Manage leaves, track balances, and view holidays — all in one place
        </p>
      </div>

      {/* Balance Cards — 2 cols on mobile, 4 on md+ */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4 md:gap-4">
        {balanceCards.map((card, index) => (
          <div
            key={index}
            className="p-3 transition-all duration-300 bg-white border border-gray-100 shadow-sm sm:p-4 dark:bg-gray-900 rounded-xl dark:border-gray-800 hover:shadow-md"
          >
            <p className="text-xs text-gray-500 truncate sm:text-sm dark:text-gray-400">{card.title}</p>
            {'value' in card ? (
              <p className="text-lg sm:text-xl font-bold text-gray-800 dark:text-white mt-0.5">{card.value}</p>
            ) : (
              <p className="text-lg sm:text-xl font-bold text-gray-800 dark:text-white mt-0.5">
                {card.total - card.used}
                <span className="text-xs font-normal text-gray-500 dark:text-gray-400"> / {card.total}</span>
              </p>
            )}
            {'used' in card && (
              <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">Used: {card.used}d</p>
            )}
          </div>
        ))}
      </div>

      {/* Action Buttons — stacked on mobile, row on sm+ */}
      <div className="flex flex-col gap-2 sm:flex-row sm:justify-end sm:gap-3">
        <button
          onClick={() => setIsModalOpen(true)}
          className="flex items-center justify-center gap-2 px-5 py-2.5 sm:px-6 sm:py-3 text-sm font-medium text-white transition-all duration-200 rounded-lg shadow-lg bg-gradient-to-r from-blue-500 to-blue-500 hover:from-blue-600 hover:to-blue-600 shadow-blue-500/25"
        >
          <Calendar className="w-4 h-4" />
          Apply Leave
        </button>
        <button
          onClick={() => setIsHolidayModalOpen(true)}
          className="flex items-center justify-center gap-2 px-5 py-2.5 sm:px-6 sm:py-3 text-sm font-medium text-white transition-all duration-200 rounded-lg shadow-lg bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 shadow-blue-500/25"
        >
          <CalendarCheck2 className="w-4 h-4" />
          View Holidays
          {holidaysData.length > 0 && (
            <span className="ml-1 bg-white text-blue-600 text-xs rounded-full px-2 py-0.5 font-semibold">
              {holidaysData.length}
            </span>
          )}
        </button>
      </div>

      {/* Leave Requests Section */}
      <div className="overflow-hidden transition-all duration-300 bg-white border border-gray-100 shadow-sm dark:bg-gray-900 rounded-xl dark:border-gray-800">

        {/* Table header + search */}
        <div className="px-4 py-4 border-b border-gray-100 sm:px-6 dark:border-gray-800">
          <div className="flex flex-col gap-3">
            <h3 className="text-sm font-semibold text-gray-800 dark:text-white sm:text-base">
              Leave Requests History
            </h3>

            {/* Search controls — stack on mobile */}
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <select
                value={searchField}
                onChange={(e) => setSearchField(e.target.value)}
                className="w-full px-3 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg sm:w-auto dark:border-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 dark:bg-gray-800 dark:text-gray-300"
              >
                <option value="all">All Fields</option>
                <option value="type">Leave Type</option>
                <option value="status">Status</option>
                <option value="reason">Reason</option>
                <option value="date">Date</option>
              </select>

              <div className="relative flex-1 sm:max-w-xs">
                <Search className="absolute w-4 h-4 text-gray-400 transform -translate-y-1/2 left-3 top-1/2 dark:text-gray-500" />
                <input
                  type="text"
                  placeholder={`Search by ${searchField === 'all' ? 'anything' : searchField}...`}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full py-2 pl-10 pr-10 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg dark:border-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 dark:bg-gray-800 dark:text-gray-300"
                />
                {searchTerm && (
                  <button
                    onClick={handleClearSearch}
                    className="absolute text-gray-400 transform -translate-y-1/2 right-3 top-1/2 hover:text-gray-600 dark:hover:text-gray-300"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>

            {searchTerm && (
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Found <span className="font-medium text-gray-700 dark:text-gray-300">{filteredLeaveData.length}</span> result(s) for "{searchTerm}"
                {searchField !== 'all' && ` in ${searchField} field`}
              </p>
            )}
          </div>
        </div>

        {/* Mobile: card list | Desktop: table */}
        {/* Mobile cards (hidden on md+) */}
        <div className="divide-y divide-gray-100 md:hidden dark:divide-gray-800">
          {filteredLeaveData.length > 0 ? (
            <div className="p-3 space-y-3">
              {filteredLeaveData.map((request) => (
                <LeaveCard
                  key={request._id}
                  request={request}
                  formatDate={formatDate}
                  getStatusColor={getStatusColor}
                />
              ))}
            </div>
          ) : (
            <div className="px-4 py-8 text-sm text-center text-gray-500 dark:text-gray-400">
              {searchTerm
                ? `No leave requests found matching "${searchTerm}"`
                : 'No leave requests found'}
            </div>
          )}
        </div>

        {/* Desktop table (hidden on mobile) */}
        <div className="hidden overflow-x-auto md:block">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-800/50">
              <tr>
                <th className="px-6 py-3 text-xs font-medium text-left text-gray-500 uppercase dark:text-gray-400">Type</th>
                <th className="px-6 py-3 text-xs font-medium text-left text-gray-500 uppercase dark:text-gray-400">From</th>
                <th className="px-6 py-3 text-xs font-medium text-left text-gray-500 uppercase dark:text-gray-400">To</th>
                <th className="px-6 py-3 text-xs font-medium text-left text-gray-500 uppercase dark:text-gray-400">Days</th>
                <th className="px-6 py-3 text-xs font-medium text-left text-gray-500 uppercase dark:text-gray-400">Reason</th>
                <th className="px-6 py-3 text-xs font-medium text-left text-gray-500 uppercase dark:text-gray-400">Applied On</th>
                <th className="px-6 py-3 text-xs font-medium text-left text-gray-500 uppercase dark:text-gray-400">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
              {filteredLeaveData.length > 0 ? (
                filteredLeaveData.map((request) => (
                  <tr key={request._id} className="transition-colors duration-200 hover:bg-gray-50 dark:hover:bg-gray-800/50">
                    <td className="px-6 py-3 text-sm text-gray-700 dark:text-gray-300">
                      {request.leaveType}
                      {request.leaveDuration === 'Half Day' && (
                        <span className="ml-1.5 text-xs text-gray-400 dark:text-gray-500">(Half Day)</span>
                      )}
                    </td>
                    <td className="px-6 py-3 text-sm text-gray-700 dark:text-gray-300">{formatDate(request.fromDate)}</td>
                    <td className="px-6 py-3 text-sm text-gray-700 dark:text-gray-300">{formatDate(request.toDate)}</td>
                    <td className="px-6 py-3 text-sm text-gray-700 dark:text-gray-300">{request.days}</td>
                    <td className="max-w-xs px-6 py-3 text-sm text-gray-700 truncate dark:text-gray-300" title={request.reason}>
                      {request.reason}
                    </td>
                    <td className="px-6 py-3 text-sm text-gray-700 dark:text-gray-300">
                      {formatDate(request.appliedAt || request.createdAt)}
                    </td>
                    <td className="px-6 py-3">
                      <span className={`inline-flex px-2 py-1 text-xs rounded-full ${getStatusColor(request.status)}`}>
                        {request.status || 'PENDING'}
                      </span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" className="px-6 py-8 text-sm text-center text-gray-500 dark:text-gray-400">
                    {searchTerm
                      ? `No leave requests found matching "${searchTerm}"`
                      : 'No leave requests found'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Info Card */}
      <div className="p-3 transition-all duration-300 border border-blue-100 sm:p-4 bg-blue-100/20 dark:bg-blue-900/20 rounded-xl dark:border-blue-800">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
          <div>
            <h4 className="mb-1 text-sm font-medium text-blue-800 dark:text-blue-300">Leave Policy Information</h4>
            <p className="text-xs sm:text-sm text-blue-700 dark:text-blue-400 space-y-0.5">
              • Leave requests should be submitted at least 3 days in advance<br />
              • Approved leaves will be deducted from your available balance
            </p>
          </div>
        </div>
      </div>

      <LeaveModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSubmit={handleSubmit}
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        loading={loading}
        calculateDays={calculateDays}
      />

      <HolidaysModal
        isOpen={isHolidayModalOpen}
        onClose={() => setIsHolidayModalOpen(false)}
        holidaysData={holidaysData}
        holidaysByMonth={holidaysByMonth}
        formatDate={formatDate}
      />
    </div>
  );
};

export default LeaveManagement;
